import { PreferencesDB } from './PreferencesDB.ts';
import { Category } from '../models/Category.ts';
import { Backup } from '../models/Backup.ts';
import { profile as localProfile, saveAndSync } from '../composable/usePreferenceCollection.ts';
import { Log } from './Log.ts';
import { getFirestore, doc, setDoc, getDoc, type Firestore } from 'firebase/firestore';
import { type User } from 'firebase/auth';
import { useCurrentUser, useDocument } from 'vuefire';
import { ref, computed, type Ref, type ComputedRef } from 'vue';
import { type DocumentData, DocumentReference } from '@firebase/firestore';
import { Profile } from '../models/Profile.ts';

const backupDocRef: Ref<DocumentReference<DocumentData, DocumentData> | undefined> = ref();
export const backupRef: Ref<any> = ref();

// Firebase document reference for the profile
const profileDocRef: Ref<DocumentReference<DocumentData, DocumentData> | undefined> = ref();
// Raw profile data from Firebase
export const profileRef: Ref<any> = ref();
// Transformed profile data as a Profile object
export const profile: ComputedRef<Profile | undefined> = computed(() => {
  if (!profileRef.value) return undefined;

  const profileData = profileRef.value;
  if (!profileData) return undefined;

  const profileInstance = new Profile();
  Object.assign(profileInstance, profileData);
  return profileInstance;
});

// Current user ID
export const userId: Ref<string> = ref('');

export async function initializeFirebaseDB() {
  const user = useCurrentUser();
  if (user.value == null) {
    console.log('Not yet logged in');
    return;
  }
  userId.value = user.value.uid;
  await restoreOrBackup();
}

export async function restoreOrBackup() {
  const user = useCurrentUser();
  if (user.value == null) {
    console.log('Not yet logged in');
    return;
  }
  const db = getFirestore();

  await restoreOrBackupProfile(db, user.value);

  if (!backupDocRef.value) {
    backupDocRef.value = doc(db, `users/${userId.value}/backup/default`);
    backupRef.value = useDocument(backupDocRef.value);
  }
  const docRef = backupDocRef.value as DocumentReference<DocumentData, DocumentData>;

  const backup = new Backup();
  backup.categories = (await PreferencesDB.getAll(Category)) as Category[];

  // Get deleted keys from both local profile and remote restore if they exist
  const localDeletedKeys = localProfile.value ? localProfile.value.deletedKeys : {};
  backup.deletedKeys = { ...localDeletedKeys };

  // Also include deleted keys from the remote profile if it was just loaded
  if (profileRef.value && profileRef.value.deletedKeys) {
    for (const id in profileRef.value.deletedKeys) {
      if (!backup.deletedKeys[id] || profileRef.value.deletedKeys[id] > backup.deletedKeys[id]) {
        backup.deletedKeys[id] = profileRef.value.deletedKeys[id];
      }
    }
  }

  const docSnap = await getDoc(backupDocRef.value);
  const restore: Backup | null = docSnap.data() as Backup;

  if (restore == null) {
    await setDoc(docRef, backup.toPlain());
    return;
  }

  if (restore.deletedKeys) {
    for (const id in restore.deletedKeys) {
      if (!backup.deletedKeys[id] || restore.deletedKeys[id] > backup.deletedKeys[id]) {
        backup.deletedKeys[id] = restore.deletedKeys[id];
      }
    }
  }

  // Categories in the remote restore that should be synced locally
  for (const category of restore.categories) {
    // If it's marked as deleted, ensure it's gone locally
    if (backup.deletedKeys[category.id]) {
      await PreferencesDB.delete(Category.name, category.id);
      backup.categories = backup.categories.filter((e) => e.id != category.id);
      continue;
    }

    const localCategory = backup.categories.find((p) => p.id === category.id);

    if (!localCategory || category.lastModified > localCategory.lastModified) {
      const obj = localCategory ?? new Category();
      Object.assign(obj, category);
      await saveAndSync(Category, obj, false);
      Log.Debug(restoreOrBackup, `Restored category #${category.id}`);

      // Update the local list for the final backup save
      const index = backup.categories.findIndex((c) => c.id === obj.id);
      if (index !== -1) {
        backup.categories[index] = obj;
      } else {
        backup.categories.push(obj);
      }
    }
  }

  // Check local categories against deletedKeys
  const activeCategories: Category[] = [];
  for (const category of backup.categories) {
    if (backup.deletedKeys[category.id]) {
      await PreferencesDB.delete(Category.name, category.id);
    } else {
      activeCategories.push(category);
    }
  }
  backup.categories = activeCategories;

  await setDoc(docRef, backup.toPlain());
}

export async function restoreOrBackupProfile(db: Firestore, user: User) {
  // Initialize the profile document reference if it's not already initialized
  if (profileDocRef.value == null) {
    const docRef = doc(db, `users/${user.uid}/profile/default`);
    profileDocRef.value = docRef;
    profileRef.value = useDocument(docRef);

    // Check if the profile document exists in Firebase
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      // If the profile doesn't exist in Firebase, create it
      const newProfile = new Profile();
      newProfile.name = user?.displayName?.split(' ')[0] || '';
      await setDoc(docRef, newProfile.toPlain());
    }
  }
}

export async function restore() {
  const user = useCurrentUser();
  if (user.value == null) {
    console.log('Not yet logged in');
    return;
  }

  const db = getFirestore();
  const docRef = doc(db, `users/${user.value.uid}/backup/default`);
  const docSnap = await getDoc(docRef);

  const backup = new Backup();
  backup.categories = (await PreferencesDB.getAll(Category)) as Category[];

  // Get deleted keys from both local profile and remote restore if they exist
  const localDeletedKeys = localProfile.value ? localProfile.value.deletedKeys : {};
  backup.deletedKeys = { ...localDeletedKeys };

  // Also include deleted keys from the remote profile if it was just loaded
  if (profileRef.value && profileRef.value.deletedKeys) {
    for (const id in profileRef.value.deletedKeys) {
      if (!backup.deletedKeys[id] || profileRef.value.deletedKeys[id] > backup.deletedKeys[id]) {
        backup.deletedKeys[id] = profileRef.value.deletedKeys[id];
      }
    }
  }

  const restore: Backup | null = docSnap.data() as Backup;

  if (restore == null) {
    return;
  }

  if (restore.deletedKeys) {
    for (const id in restore.deletedKeys) {
      if (!backup.deletedKeys[id] || restore.deletedKeys[id] > backup.deletedKeys[id]) {
        backup.deletedKeys[id] = restore.deletedKeys[id];
      }
    }
  }

  for (const category of restore.categories) {
    if (backup.deletedKeys[category.id]) {
      await PreferencesDB.delete(Category.name, category.id);
      backup.categories = backup.categories.filter((e) => e.id != category.id);
      continue;
    }

    const localCategory = backup.categories.find((p) => p.id === category.id);

    if (!localCategory || category.lastModified > localCategory.lastModified) {
      const obj = localCategory ?? new Category();
      Object.assign(obj, category);
      await saveAndSync(Category, obj, false);
      Log.Debug(restoreOrBackup, `Restored category #${category.id}`);
    }
  }

  for (const category of backup.categories) {
    if (backup.deletedKeys[category.id]) {
      await PreferencesDB.delete(Category.name, category.id);
    }
  }
}
